import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { NetworkIcon, TrendingDownIcon, SearchIcon } from 'lucide-react';

// De drie uitgangssituaties waarin bedrijven bij ons binnenkomen.
// Eerder stonden hier de vissers-archetypen met emoji in de kop. De
// diagnose was goed, de verpakking paste niet bij de doelgroep.
const pains = [{
  icon: NetworkIcon,
  title: 'Je groeit op doorverwijzingen',
  body: 'Het werk komt binnen via je netwerk en via klanten die je aanbevelen. Dat heeft je gebracht waar je nu bent, maar je kunt het niet sturen en niet opschalen. Wie er dit kwartaal belt, hangt af van wie er aan je denkt.',
  accent: '#FF5F57'
}, {
  icon: TrendingDownIcon,
  title: 'Je hebt geïnvesteerd zonder bewijs te krijgen',
  body: 'Er ging budget naar advertenties of naar een bureau. Er kwamen rapportages met vertoningen en posities, maar niemand kon zeggen welke zoekopdracht daadwerkelijk een klant opleverde. De kosten waren zichtbaar, het rendement niet.',
  accent: '#FEBC2E'
}, {
  icon: SearchIcon,
  title: 'Je site is gebouwd op aannames',
  body: 'Er staat een uitgebreide website, opgezet rond zoekwoorden uit een tool. Veel volume op papier, weinig aanvragen in de praktijk. Er is geen meting die aantoont of die zoekwoorden in jouw markt ook kopers zijn.',
  accent: '#0066FF'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.23, 1, 0.32, 1]
    }
  }
};
export function PainPoints() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-20 md:py-24 bg-white" data-id="element-281" data-mp="30jh4d562fs8u">
      <div className="max-w-[1200px] mx-auto px-6" data-id="element-282" data-mp="2vhrq7gnjqygj">
        <motion.div className="max-w-3xl mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-283" data-mp="1t55ac7kxivvy">
          <div className="section-label" data-id="element-284" data-mp="2744xtbwph4qj">De uitgangssituatie</div>
          <h2 className="text-[clamp(2rem,3.6vw,2.8rem)] font-bold text-brand-dark leading-tight tracking-tight mb-4" data-id="element-285" data-mp="1afovef8hz0w0">
            Drie situaties waarin bedrijven bij ons binnenkomen
          </h2>
          <p className="text-brand-gray text-lg leading-relaxed" data-id="element-286" data-mp="1jeanp23rtu9z">
            Ze verschillen in aanpak, maar delen hetzelfde probleem: er is geen
            meting die aantoont welke investering geld oplevert.
          </p>
        </motion.div>

        <motion.div className="grid md:grid-cols-3 gap-6 items-stretch" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-287" data-mp="qotz6fj9uxj1">
          {pains.map(pain => <motion.div key={pain.title} className="card flex flex-col" variants={itemVariants} data-id="element-288" data-mp="j6vtyn84i8lm">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{
            background: `${pain.accent}15`,
            border: `1px solid ${pain.accent}30`
          }} data-id="element-289" data-mp="ea5adpx40uvg">
                <pain.icon size={22} style={{
              color: pain.accent
            }} data-id="element-290" data-mp="f30krp56ldd" />
              </div>
              <h3 className="font-bold text-lg text-brand-dark mb-4" data-id="element-291" data-mp="3rnql55fk64jv">
                {pain.title}
              </h3>
              <p className="text-brand-gray leading-relaxed text-[0.96rem]" data-id="element-292" data-mp="3mpp40q1p0fb1">
                {pain.body}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Overgang naar de aanpak */}
        <motion.div className="mt-14 rounded-2xl border border-[#E8EDF8] bg-[#F8FAFC] p-8 md:p-10 max-w-4xl" initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-293" data-mp="3dpjvgckrjava">
          <p className="text-[1.05rem] text-brand-gray leading-relaxed" data-id="element-294" data-mp="xv0rfkyfeawd">
            Wat in alle drie ontbreekt, is de stap ervoor.{' '}
            <strong className="text-brand-dark font-semibold" data-id="element-295" data-mp="12t29c2k48469">
              Vaststellen welke vraag er in de markt is voordat er geld naartoe
              gaat.
            </strong>{' '}
            Dat is wat CorePrint doet.
          </p>
        </motion.div>
      </div>
    </section>;
}